import { create } from 'zustand';
import * as model from '../lib/watchModel';
import type { GenerationSummary } from './types';
import { cancelGenerationDetailRefresh } from './detailRefresh';
import { forgetKnownSummaries } from './knownSummaries';

interface GenerationWatchStore extends model.WatchState {
  /** Строка создания смонтирована на главной — новый заход. */
  enterHome: () => void;
  leaveHome: () => void;
  /** Успешное «Создать» или «Добавить» на главной; `restart` — «Повторить» в записи (ТЗ § 23.4, п. 2). */
  watch: (summary: GenerationSummary, cardIds: readonly string[], options?: model.WatchOptions) => void;
  updateSummary: (summary: GenerationSummary) => void;
  markAnnounced: (cardIds: readonly string[]) => void;
  dismiss: (id: string) => void;
  forget: (id: string) => void;
  /** Список: строки записей доехали (въезд закончился) — момент вставки для решения сигнала через 300 мс. */
  markInserted: (ids: readonly string[]) => void;
  /** Список: строки записей появились в DOM и ещё могут въезжать — запасной срок от ответа не действует. */
  markMounted: (ids: readonly string[]) => void;
  /** Список: строка записи вошла на экран или ушла с него (над полосой видно ≥ 60 %). */
  observeRow: (id: string, visible: boolean) => void;
  /** Сигнал у строки: раскрыть запись и прокрутить к ней. */
  requestReveal: (id: string) => void;
  /** Список выполнил запрос раскрытия. */
  consumeReveal: (at?: number) => void;
  /** Строка создания: открыты ли лист, превью, этап 2 или меню модулей; `settleMs` — через сколько после
   *  закрытия сигнал может вернуться (телефон — 120 мс после ухода этапа 2, ТЗ § 13). */
  setOverlay: (open: boolean, settleMs?: number) => void;
  /** Строка создания: клавиатура открыта (фокус в поле на сенсорном устройстве). */
  setKeyboard: (open: boolean) => void;
  /** Шаг модели сигнала: решение о сигнале и принятие итога (ТЗ § 8). */
  stepSignal: (summaryOf: model.SummaryOf, input: model.SignalInput) => void;
  /** Нажали сигнал: раскрыть запись, законченную генерацию — принять. */
  pressSignal: (id: string, summary: GenerationSummary | null) => void;
  /** Live-область объявила начало или итог. */
  markSpoken: (items: readonly Pick<model.SpeechItem, 'id' | 'phase'>[]) => void;
}

/**
 * Слежение за нажатиями «Создать» на главной (ТЗ главной § 8). Стор в памяти; логика —
 * в чистой модели `lib/watchModel.ts`. Обнуляется при смене пользователя (`app/main.tsx`).
 */
export const useGenerationWatch = create<GenerationWatchStore>()((set) => ({
  ...model.initialWatchState,
  enterHome: () => set((s) => model.enterHome(s)),
  leaveHome: () => set((s) => model.leaveHome(s)),
  watch: (summary, cardIds, options) => set((s) => model.watchGeneration(s, summary, cardIds, Date.now(), options)),
  updateSummary: (summary) => set((s) => model.updateWatchedSummary(s, summary)),
  markAnnounced: (cardIds) => set((s) => model.markAnnounced(s, cardIds)),
  dismiss: (id) => set((s) => model.dismissWatched(s, id)),
  forget: (id) => set((s) => model.forgetWatched(s, id)),
  markInserted: (ids) => set((s) => model.markInserted(s, ids, Date.now())),
  markMounted: (ids) => set((s) => model.markMounted(s, ids, Date.now())),
  observeRow: (id, visible) => set((s) => model.observeRowView(s, id, visible, Date.now())),
  requestReveal: (id) => set((s) => model.requestReveal(s, id, Date.now())),
  consumeReveal: (at) => set((s) => model.consumeReveal(s, at)),
  setOverlay: (open, settleMs) => set((s) => model.setOverlay(s, open, Date.now(), settleMs)),
  setKeyboard: (open) => set((s) => model.setKeyboard(s, open)),
  stepSignal: (summaryOf, input) => set((s) => model.stepSignal(s, summaryOf, input, Date.now())),
  pressSignal: (id, summary) => set((s) => model.pressSignal(s, id, summary, Date.now())),
  markSpoken: (items) => set((s) => model.markSpoken(s, items)),
}));

/** Карточка из отслеживаемой генерации (любого захода) — для решения о тосте. */
export const isWatchedCard = (cardId: string) => model.isWatchedCard(useGenerationWatch.getState(), cardId);

/**
 * Тост о карточках не показываем: главная открыта, и все они — из отслеживаемых генераций (ТЗ § 8).
 * Решать в момент показа: SSE бывает раньше ответа «Создать».
 */
export const shouldMuteGenerationToast = (cardIds: readonly string[]) =>
  model.shouldMuteToast(useGenerationWatch.getState(), cardIds);

/** Тост о карточках всё же показан — их записи помечаются `announced` (итог больше не объявляется). */
export const markGenerationToastShown = (cardIds: readonly string[]) => useGenerationWatch.getState().markAnnounced(cardIds);

/**
 * Сменился пользователь: без этого второй ученик в той же вкладке увидел бы в сигнале слово
 * первого из запасной сводки. Кэш запросов чистит `qc.clear()` при входе и выходе.
 */
export function resetGenerationWatch(): void {
  useGenerationWatch.setState(model.initialWatchState);
  cancelGenerationDetailRefresh();
  forgetKnownSummaries();
}
